import { cs } from '../../lib/currency';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, getTodayInTz } from '../../utils/businessTime';
import {
  Users, DollarSign, Receipt, Shield, Clock, Download, BarChart3, Coins,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TournamentGames } from '../../components/admin/TournamentGames';
import TeamChat from '../../components/chat/TeamChat';
import {
  PaymentDialog,
  ExpenseDialog,
  TeamDialog,
  AddPlayersDialog,
  ManageRosterDialog,
  DeleteTeamDialog,
  DeleteRegistrationDialog,
  AddToTeamDialog,
  BulkAddToTeamDialog,
  BulkMarkPaidDialog,
  ExportRosterDialog,
  ManageExpenseCategoriesDialog,
  RegistrationsTab,
  TeamsTab,
  ExpensesTab,
  PlayerSummaryTab,
  CostAllocationTab,
  SummaryTab,
  TournamentDetailHeader,
  TournamentStats,
} from '../../components/admin/tournament-detail';
import RefundDialog from '../../components/admin/RefundDialog';
import { AdminTournamentFormDialog } from '../../components/tournaments/AdminTournamentFormDialog';
import TournamentAmountChangeConfirmDialog from '../../components/admin/TournamentAmountChangeConfirmDialog';
import useTournamentEditDialog from '../../hooks/useTournamentEditDialog';

const TournamentDetail = () => {
  const businessTz = useBusinessTimezone();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin';
  const isStaff = role === 'staff';
  // Staff have parity with admin for tournament management EXCEPT refunds
  // (kept admin-only by explicit business rule). isAdmin remains a strict
  // admin flag for refund / "danger zone" controls. Use isStaffOrAdmin for
  // everything else (mark paid, charge card, send reminders, manage roster,
  // edit, delete, expenses, etc.).
  const isStaffOrAdmin = isAdmin || isStaff;
  const isCoach = role === 'coach';
  // After-action navigation target depends on role — staff doesn't have
  // access to /admin/tournaments (admin-only listing), so send them back
  // to their own list at /staff/tournaments.
  const tournamentsListPath = isAdmin ? '/admin/tournaments' : '/staff/tournaments';

  // ==================== Core Data State ====================
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  // iter342 — report data powering the Player Summary & Cost Allocation tabs.
  const [reportData, setReportData] = useState(null);
  const [reportDataLoading, setReportDataLoading] = useState(false);

  // ==================== Filter/Sort State ====================
  const [registrationsAgeFilter, setRegistrationsAgeFilter] = useState('all');
  const [registrationsPaymentFilter, setRegistrationsPaymentFilter] = useState('all');
  const [registrationsTeamFilter, setRegistrationsTeamFilter] = useState('all');
  const [registrationsSortField, setRegistrationsSortField] = useState('player_name');
  const [registrationsSortOrder, setRegistrationsSortOrder] = useState('asc');
  // iter307ai — Toggle to surface archived registrations (hidden by default).
  const [showArchived, setShowArchived] = useState(false);
  const [teamsAgeFilter, setTeamsAgeFilter] = useState('all');
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);

  // Refund dialog state (uses reusable RefundDialog component)
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundRegistration, setRefundRegistration] = useState(null);

  // ==================== Dialog Visibility State ====================
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showAddPlayersDialog, setShowAddPlayersDialog] = useState(false);
  const [showManagePlayersDialog, setShowManagePlayersDialog] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [coaches, setCoaches] = useState([]);

  // Add Players Dialog State
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [selectedEnrolledPlayerIds, setSelectedEnrolledPlayerIds] = useState([]);
  const [addPlayersCategory, setAddPlayersCategory] = useState('');
  const [addPlayersCategories, setAddPlayersCategories] = useState([]);
  const [notifyParents, setNotifyParents] = useState(true);
  const [loadingEnrolledPlayers, setLoadingEnrolledPlayers] = useState(false);
  const [addingPlayers, setAddingPlayers] = useState(false);
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [playerSortField, setPlayerSortField] = useState('full_name');
  const [playerSortOrder, setPlayerSortOrder] = useState('asc');

  // Manage Roster Dialog State
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [managePlayersSort, setManagePlayersSort] = useState({ field: 'player_name', order: 'asc' });

  // Delete Team Dialog State
  const [deleteTeamConfirmOpen, setDeleteTeamConfirmOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [teamHasPlayers, setTeamHasPlayers] = useState(false);
  const [teamPlayerCount, setTeamPlayerCount] = useState(0);

  // Delete Registration Dialog State
  const [deleteRegistrationDialog, setDeleteRegistrationDialog] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [loadingRegistrationDetails, setLoadingRegistrationDetails] = useState(false);
  const [deletingRegistration, setDeletingRegistration] = useState(false);
  const [refundCheckbox, setRefundCheckbox] = useState(false);
  // Admin-supplied notes to include in the parent's withdrawal email
  // (rendered as the `{{reason}}` / `{{notes}}` variable of the seeded
  // `tournament_withdrawal` template).
  const [deleteNotes, setDeleteNotes] = useState('');

  // Add to Team Dialog State
  const [addToTeamDialog, setAddToTeamDialog] = useState(false);
  const [addToTeamRegistration, setAddToTeamRegistration] = useState(null);
  const [addToTeamSelectedTeam, setAddToTeamSelectedTeam] = useState('');
  const [addToTeamWarnings, setAddToTeamWarnings] = useState([]);
  const [addToTeamLoading, setAddToTeamLoading] = useState(false);
  const [addToTeamRequiresOverride, setAddToTeamRequiresOverride] = useState(false);

  // Bulk Add to Team State
  const [bulkAddToTeamDialog, setBulkAddToTeamDialog] = useState(false);
  const [bulkAddToTeamSelectedTeam, setBulkAddToTeamSelectedTeam] = useState('');
  const [bulkAddToTeamLoading, setBulkAddToTeamLoading] = useState(false);
  const [bulkAddToTeamOverride, setBulkAddToTeamOverride] = useState(false);

  // Bulk Mark Paid State
  const [bulkMarkPaidDialog, setBulkMarkPaidDialog] = useState(false);
  const [bulkMarkPaidMethod, setBulkMarkPaidMethod] = useState('');
  const [bulkMarkPaidNotes, setBulkMarkPaidNotes] = useState('');
  const [bulkMarkPaidLoading, setBulkMarkPaidLoading] = useState(false);

  // Teams tab state
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [teamChatOpen, setTeamChatOpen] = useState(null);
  const [teamRosterSort, setTeamRosterSort] = useState({ field: 'player_name', order: 'asc' });
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportTeam, setExportTeam] = useState(null);
  const [exportFields, setExportFields] = useState({
    player_name: true, date_of_birth: true, age: true, gender: false,
    parent_name: true, parent_email: true, parent_phone: true, payment_status: true,
    skill_level: false, cricclubs_id: false, cricclubs_email: false,
    shirt_name: false, shirt_number: false, chest_size: false,
    shirt_length: false, waist_size: false, trouser_length: false,
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Form State
  const [paymentForm, setPaymentForm] = useState({ payment_method: '', amount: 0, notes: '' });
  const [teamForm, setTeamForm] = useState({ name: '', age_category: '', coach_id: '', division: '', cricclubs_url: '' });
  const [expenseForm, setExpenseForm] = useState({ category: '', description: '', amount: 0, age_category: '', expense_date: getTodayInTz('America/New_York'), team_id: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showManageCategoriesDialog, setShowManageCategoriesDialog] = useState(false);

  // ==================== Data Fetching ====================
  const fetchTournament = useCallback(async () => {
    try {
      const res = await api.get(`/tournaments/${id}`);
      setTournament(res.data);
      // Also refresh teams when tournament is refreshed
      const teamsRes = await api.get(`/tournaments/${id}/teams`);
      setTeams(teamsRes.data);
    } catch { navigate(tournamentsListPath); }
  }, [id, navigate, tournamentsListPath]);

  // iter270: inline "Edit Tournament" support from the detail page.
  const tournamentEdit = useTournamentEditDialog({
    onSaved: () => fetchTournament(),
  });

  const fetchRegistrations = useCallback(async () => {
    try {
      const params = showArchived ? '?include_archived=true' : '';
      const res = await api.get(`/tournaments/${id}/registrations${params}`);
      setRegistrations(res.data);
    } catch { console.error('Failed to fetch registrations'); }
  }, [id, showArchived]);

  const fetchTeams = useCallback(async () => {
    try { const res = await api.get(`/tournaments/${id}/teams`); setTeams(res.data); } catch { console.error('Failed to fetch teams'); }
  }, [id]);

  const fetchExpenses = useCallback(async () => {
    try {
      const [expRes, sumRes] = await Promise.all([api.get(`/tournaments/${id}/expenses`), api.get(`/tournaments/${id}/expenses/summary`)]);
      setExpenses(expRes.data);
      setExpenseSummary(sumRes.data);
    } catch { console.error('Failed to fetch expenses'); }
  }, [id]);

  const fetchReportData = useCallback(async () => {
    setReportDataLoading(true);
    try {
      const res = await api.get(`/tournaments/${id}/report-data`);
      setReportData(res.data);
    } catch { console.error('Failed to fetch report data'); }
    finally { setReportDataLoading(false); }
  }, [id]);

  const fetchCoaches = useCallback(async () => {    try { const res = await api.get('/coaches'); setCoaches(res.data); } catch { console.error('Failed to fetch coaches'); }
  }, []);

  const fetchExpenseCategories = useCallback(async () => {
    try { const res = await api.get('/tournaments/config/expense-categories'); setExpenseCategories(res.data); } catch { console.error('Failed to fetch expense categories'); }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTournament(), fetchRegistrations(), fetchTeams(), fetchExpenses(), fetchCoaches(), fetchExpenseCategories()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTournament, fetchRegistrations, fetchTeams, fetchExpenses, fetchCoaches, fetchExpenseCategories]);

  // ==================== Computed Values ====================
  const filteredRegistrations = registrations.filter(r => {
    const ageMatch = registrationsAgeFilter === 'all' || r.age_category === registrationsAgeFilter;
    const paymentMatch = registrationsPaymentFilter === 'all' || (registrationsPaymentFilter === 'unpaid' && (!r.payment_status || r.payment_status === 'unpaid')) || r.payment_status === registrationsPaymentFilter;
    const teamMatch = registrationsTeamFilter === 'all' || (registrationsTeamFilter === 'unassigned' && !r.team_name) || (registrationsTeamFilter === 'assigned' && r.team_name) || r.team_id === registrationsTeamFilter;
    return ageMatch && paymentMatch && teamMatch;
  }).sort((a, b) => {
    let aVal, bVal;
    const categoryOrder = { 'u9': 1, 'u11': 2, 'u13': 3, 'u15': 4, 'u17': 5, 'u19': 6, 'open': 7 };
    const paymentOrder = { 'unpaid': 1, 'pending': 2, 'paid': 3, 'refunded': 4 };
    if (registrationsSortField === 'player_name') { aVal = (a.player_name || '').toLowerCase(); bVal = (b.player_name || '').toLowerCase(); }
    else if (registrationsSortField === 'age_category') { aVal = categoryOrder[a.age_category?.toLowerCase()] || 99; bVal = categoryOrder[b.age_category?.toLowerCase()] || 99; }
    else if (registrationsSortField === 'payment_status') { aVal = paymentOrder[a.payment_status || 'unpaid'] || 99; bVal = paymentOrder[b.payment_status || 'unpaid'] || 99; }
    else if (registrationsSortField === 'registered_at') { aVal = new Date(a.registered_at || a.created_at || 0).getTime(); bVal = new Date(b.registered_at || b.created_at || 0).getTime(); }
    else if (registrationsSortField === 'team_name') { aVal = a.team_name ? a.team_name.toLowerCase() : 'zzz'; bVal = b.team_name ? b.team_name.toLowerCase() : 'zzz'; }
    else { aVal = a[registrationsSortField] || ''; bVal = b[registrationsSortField] || ''; }
    if (typeof aVal === 'string' && typeof bVal === 'string') return registrationsSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    return registrationsSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const filteredTeams = teams.filter(t => teamsAgeFilter === 'all' || t.age_category === teamsAgeFilter);

  const totalCollected = registrations.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (r.payment_amount || 0), 0);
  const totalRegistered = registrations.length;
  const paidCount = registrations.filter(r => r.payment_status === 'paid').length;
  const unpaidCount = registrations.filter(r => !r.payment_status || r.payment_status === 'unpaid' || r.payment_status === 'pending').length;

  // ==================== Sort Helpers ====================
  const sortTeamRoster = (players) => {
    if (!players?.length) return [];
    return [...players].sort((a, b) => {
      let aVal, bVal;
      if (teamRosterSort.field === 'player_name') { aVal = (a.player_name || a.full_name || '').toLowerCase(); bVal = (b.player_name || b.full_name || '').toLowerCase(); }
      else if (teamRosterSort.field === 'payment_status') { const o = { 'paid': 1, 'pending': 2, 'unpaid': 3 }; aVal = o[a.payment_status] || 99; bVal = o[b.payment_status] || 99; }
      else { aVal = a[teamRosterSort.field] || ''; bVal = b[teamRosterSort.field] || ''; }
      if (typeof aVal === 'string' && typeof bVal === 'string') return teamRosterSort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return teamRosterSort.order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const toggleRosterSort = (field) => setTeamRosterSort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));

  const sortAvailablePlayers = (players) => {
    if (!players?.length) return [];
    return [...players].sort((a, b) => {
      let aVal, bVal;
      if (managePlayersSort.field === 'player_name') { aVal = (a.player_name || a.full_name || '').toLowerCase(); bVal = (b.player_name || b.full_name || '').toLowerCase(); }
      else if (managePlayersSort.field === 'parent_name') { aVal = (a.parent_name || '').toLowerCase(); bVal = (b.parent_name || '').toLowerCase(); }
      else if (managePlayersSort.field === 'payment_status') { const o = { 'paid': 1, 'pending': 2, 'unpaid': 3 }; aVal = o[a.payment_status] || 99; bVal = o[b.payment_status] || 99; }
      else { aVal = a[managePlayersSort.field] || ''; bVal = b[managePlayersSort.field] || ''; }
      if (typeof aVal === 'string' && typeof bVal === 'string') return managePlayersSort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return managePlayersSort.order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const toggleManagePlayersSort = (field) => setManagePlayersSort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));

  // ==================== Handler Functions ====================
  const openPaymentDialog = (reg) => {
    // PR 19y5 — Record initial amount so PaymentDialog can detect
    // overpayment even when the admin edits `amount` upward.
    const initAmt = reg?.balance_due ?? reg?.amount ?? tournament?.initial_payment_amount ?? 0;
    setSelectedRegistration(reg);
    setPaymentForm({ payment_method: '', amount: initAmt, notes: '', _initialAmount: initAmt, overage_action: 'credit' });
    setShowPaymentDialog(true);
  };
  const handleMarkPaid = async () => {
    if (!paymentForm.payment_method) { toast.error('Please select a payment method'); return; }
    try {
      // PR 19y5 — Overpayment: cap `amount` at feeReference and forward
      // the overage as `overage_credit_amount`. Ignored for card /
      // stripe / account_credit methods.
      const feeRef = Number(
        selectedRegistration?.balance_due
        ?? selectedRegistration?.amount
        ?? paymentForm?._initialAmount
        ?? 0
      );
      const entered = Number(paymentForm.amount || 0);
      const isOffline = paymentForm.payment_method
        && !['card', 'stripe', 'account_credit'].includes(paymentForm.payment_method);
      const overage = isOffline && feeRef > 0 && entered > feeRef + 0.005
        ? +(entered - feeRef).toFixed(2)
        : 0;
      const wantCredit = paymentForm.overage_action !== 'ignore';
      const payload = {
        payment_method: paymentForm.payment_method,
        amount: overage > 0 ? feeRef : entered,
        notes: paymentForm.notes,
        overage_credit_amount: overage > 0 && wantCredit ? overage : 0,
      };
      await api.post(`/tournaments/${id}/registrations/${selectedRegistration.id}/mark-paid`, payload);
      if (overage > 0 && wantCredit) {
        toast.success(`Payment recorded — ${cs()}${overage.toFixed(2)} overage credited to parent's account`);
      } else {
        toast.success('Payment recorded');
      }
      setShowPaymentDialog(false); fetchRegistrations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleBulkPaymentReminder = async () => {
    if (selectedRegistrations.length === 0) { toast.error('Please select registrations first'); return; }
    try { const res = await api.post(`/tournaments/${id}/bulk/send-payment-reminders`, { registration_ids: selectedRegistrations }); toast.success(`${res.data.sent} payment reminders sent`); setSelectedRegistrations([]); } catch { toast.error('Failed to send reminders'); }
  };

  const handleBulkAddToTeam = async () => {
    if (!bulkAddToTeamSelectedTeam || selectedRegistrations.length === 0) { toast.error('Please select a team and players'); return; }
    setBulkAddToTeamLoading(true);
    try {
      const res = await api.post(`/tournaments/${id}/bulk/add-to-team`, { registration_ids: selectedRegistrations, team_id: bulkAddToTeamSelectedTeam, override_warnings: bulkAddToTeamOverride });
      if (res.data.success) {
        if (res.data.added > 0) toast.success(`${res.data.added} player(s) added to ${res.data.team_name}`);
        if (res.data.skipped > 0) toast.info(`${res.data.skipped} player(s) skipped (already on team)`);
        if (res.data.has_warnings && !bulkAddToTeamOverride) toast.warning(`Some players have eligibility warnings. Enable "Override Warnings" to add them.`);
        setBulkAddToTeamDialog(false); setBulkAddToTeamSelectedTeam(''); setBulkAddToTeamOverride(false); setSelectedRegistrations([]); fetchTeams();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add players to team'); } finally { setBulkAddToTeamLoading(false); }
  };

  const handleBulkMarkPaid = async () => {
    if (!bulkMarkPaidMethod || selectedRegistrations.length === 0) { toast.error('Please select method and registrations'); return; }
    setBulkMarkPaidLoading(true);
    try {
      const res = await api.post(`/tournaments/${id}/bulk/mark-paid`, { registration_ids: selectedRegistrations, payment_method: bulkMarkPaidMethod, notes: bulkMarkPaidNotes || 'Bulk payment' });
      if (res.data.success) {
        if (res.data.marked_paid > 0) toast.success(`${res.data.marked_paid} registration(s) marked as paid (${cs()}${res.data.total_amount.toFixed(2)})`);
        if (res.data.already_paid > 0) toast.info(`${res.data.already_paid} registration(s) were already paid`);
        setBulkMarkPaidDialog(false); setBulkMarkPaidMethod(''); setBulkMarkPaidNotes(''); setSelectedRegistrations([]); fetchRegistrations();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark paid'); } finally { setBulkMarkPaidLoading(false); }
  };

  // Expense handlers
  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount) { toast.error('Please fill in required fields'); return; }
    try {
      const payload = { category: expenseForm.category, description: expenseForm.description, amount: expenseForm.amount, expense_date: expenseForm.expense_date || getTodayInTz(businessTz) };
      if (expenseForm.age_category && expenseForm.age_category !== 'general') payload.age_category = expenseForm.age_category;
      if (expenseForm.team_id) payload.team_id = expenseForm.team_id;
      if (editingExpense) { await api.put(`/tournaments/${id}/expenses/${editingExpense.id}`, payload); toast.success('Expense updated'); }
      else { await api.post(`/tournaments/${id}/expenses`, payload); toast.success('Expense added'); }
      setShowExpenseDialog(false); setEditingExpense(null); setExpenseForm({ category: '', description: '', amount: 0, age_category: '', expense_date: getTodayInTz(businessTz), team_id: '' }); fetchExpenses();
    } catch { toast.error(editingExpense ? 'Failed to update expense' : 'Failed to add expense'); }
  };
  const openEditExpenseDialog = (expense) => { setEditingExpense(expense); setExpenseForm({ category: expense.category, description: expense.description || '', amount: expense.amount, age_category: expense.age_category || 'general', expense_date: expense.expense_date || getTodayInTz(businessTz), team_id: expense.team_id || '' }); setShowExpenseDialog(true); };
  const openAddExpenseDialog = () => { setEditingExpense(null); setExpenseForm({ category: '', description: '', amount: 0, age_category: '', expense_date: getTodayInTz(businessTz), team_id: '' }); setShowExpenseDialog(true); };
  const handleDeleteExpense = async (expenseId) => { try { await api.delete(`/tournaments/${id}/expenses/${expenseId}`); toast.success('Expense deleted'); fetchExpenses(); } catch { toast.error('Failed to delete expense'); } };

  // Team handlers
  const openCreateTeamDialog = () => { setSelectedTeam(null); setTeamForm({ name: '', age_category: '', coach_id: '', division: '', cricclubs_url: '' }); setShowTeamDialog(true); };
  const openEditTeamDialog = (team) => { setSelectedTeam(team); setTeamForm({ name: team.name, age_category: team.age_category, coach_id: team.coach_id || '', division: team.division || '', cricclubs_url: team.cricclubs_url || '' }); setShowTeamDialog(true); };
  const handleCreateTeam = async () => {
    if (!teamForm.name) { toast.error('Please enter a team name'); return; }
    try { await api.post(`/tournaments/${id}/teams`, { name: teamForm.name, age_category: teamForm.age_category || null, coach_id: teamForm.coach_id || null, division: teamForm.division || null, cricclubs_url: teamForm.cricclubs_url || null }); toast.success('Team created'); setShowTeamDialog(false); fetchTeams(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create team'); }
  };
  const handleUpdateTeam = async () => {
    if (!teamForm.name) { toast.error('Please enter a team name'); return; }
    try { await api.put(`/tournaments/${id}/teams/${selectedTeam.id}`, { name: teamForm.name, age_category: teamForm.age_category || null, coach_id: teamForm.coach_id || null, division: teamForm.division || null, cricclubs_url: teamForm.cricclubs_url || null }); toast.success('Team updated'); setShowTeamDialog(false); fetchTeams(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update team'); }
  };

  const openDeleteTeamConfirmation = (team, e) => { if (e) { e.preventDefault(); e.stopPropagation(); } setTeamToDelete(team); setTeamHasPlayers(team.player_count > 0); setTeamPlayerCount(team.player_count || 0); setDeleteTeamConfirmOpen(true); };
  const handleConfirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    setDeletingTeam(true);
    try { if (teamHasPlayers) { await api.delete(`/tournaments/${id}/teams/${teamToDelete.id}?force=true`); toast.success(`Team deleted. ${teamPlayerCount} player(s) released.`); } else { await api.delete(`/tournaments/${id}/teams/${teamToDelete.id}`); toast.success('Team deleted'); } fetchTeams(); setDeleteTeamConfirmOpen(false); setTeamToDelete(null); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete team'); } finally { setDeletingTeam(false); }
  };

  // Delete Registration
  const openDeleteRegistrationDialog = async (registration) => {
    setRegistrationToDelete(registration); setRefundCheckbox(false); setDeleteNotes(''); setLoadingRegistrationDetails(true); setDeleteRegistrationDialog(true);
    try { const res = await api.get(`/tournaments/${id}/registrations/${registration.id}/details`); setRegistrationDetails(res.data); } catch { setRegistrationDetails(null); } finally { setLoadingRegistrationDetails(false); }
  };
  const handleDeleteRegistration = async () => {
    if (!registrationToDelete) return;
    setDeletingRegistration(true);
    try {
      const params = new URLSearchParams();
      if (registrationToDelete.payment_status === 'paid' && refundCheckbox) params.append('refund', 'true');
      if (deleteNotes && deleteNotes.trim()) params.append('notes', deleteNotes.trim());
      const res = await api.delete(`/tournaments/${id}/registrations/${registrationToDelete.id}/admin${params.toString() ? '?' + params.toString() : ''}`);
      const verb = res.data.soft_cancelled ? 'cancelled' : 'deleted';
      if (res.data.refund) toast.success(`${verb === 'cancelled' ? 'Registration cancelled' : 'Deleted'}. Refund of ${cs()}${res.data.refund.amount} processed.`);
      else toast.success(`Registration for ${res.data.player_name} ${verb}.`);
      fetchRegistrations(); setDeleteRegistrationDialog(false); setRegistrationToDelete(null); setRegistrationDetails(null); setDeleteNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete registration'); } finally { setDeletingRegistration(false); }
  };

  // Add to Team
  const openAddToTeamDialog = (reg) => { setAddToTeamRegistration(reg); setAddToTeamSelectedTeam(''); setAddToTeamWarnings([]); setAddToTeamRequiresOverride(false); setAddToTeamDialog(true); };
  const handleAddToTeam = async (overrideWarnings = false) => {
    if (!addToTeamSelectedTeam) { toast.error('Please select a team'); return; }
    setAddToTeamLoading(true);
    try {
      const res = await api.post(`/tournaments/${id}/teams/${addToTeamSelectedTeam}/add-player-with-validation?player_id=${addToTeamRegistration.player_id}&override_warnings=${overrideWarnings}`);
      if (res.data.success) { toast.success(res.data.message); setAddToTeamDialog(false); fetchTeams(); }
      else if (res.data.requires_override) { setAddToTeamWarnings(res.data.warnings || []); setAddToTeamRequiresOverride(true); }
      else { toast.error(res.data.message || 'Failed'); }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail.message) { toast.error(detail.message); if (detail.eligibility) { setAddToTeamWarnings([{ type: 'age_ineligible', message: detail.eligibility.warning || 'Age requirements not met', severity: 'error' }]); setAddToTeamRequiresOverride(true); } }
      else toast.error(detail || 'Failed to add player to team');
    } finally { setAddToTeamLoading(false); }
  };
  const getTeamsForRegistration = (registration) => {
    if (!registration || !teams.length) return [];
    const regCat = registration.age_category?.toLowerCase();
    return teams.map(t => ({ ...t, isMatchingCategory: t.age_category?.toLowerCase() === regCat }));
  };

  // Add Players to Tournament
  const [includeUnenrolledPlayers, setIncludeUnenrolledPlayers] = useState(false);
  const fetchEnrolledPlayersForTournament = async (categories, includeUnenrolled = includeUnenrolledPlayers) => {
    setLoadingEnrolledPlayers(true);
    try {
      const hasCats = (tournament?.age_categories || []).length > 0;
      // iter350 — only require a category selection when the tournament
      // actually HAS categories. No-category tournaments list all players.
      if (hasCats && Array.isArray(categories) && categories.length === 0) { setEnrolledPlayers([]); return; }
      const params = includeUnenrolled ? '?include_unenrolled=true' : '';
      const res = await api.get(`/tournaments/${id}/available-players${params}`);
      let players = res.data;
      if (hasCats && Array.isArray(categories) && categories.length > 0) {
        // When the admin has opted into the "include unenrolled" pool, also keep
        // players whose `eligible_categories` is empty — these are graduates /
        // older players who aged out (or fall outside the tournament's date_range
        // window) and need an admin override to register. The Dialog renders an
        // "Override" badge for them so the admin can knowingly pick them.
        players = players.filter(p => {
          const eligible = p.eligible_categories || [];
          if (eligible.length === 0) return includeUnenrolled;
          return categories.some(cat => eligible.includes(cat));
        });
      }
      setEnrolledPlayers(players);
    } catch { setEnrolledPlayers([]); } finally { setLoadingEnrolledPlayers(false); }
  };
  const openAddPlayersDialog = async () => {
    setSelectedEnrolledPlayerIds([]); const allCats = tournament.age_categories || [];
    setAddPlayersCategories(allCats); setAddPlayersCategory(allCats[0] || '');
    setIncludeUnenrolledPlayers(false);
    setNotifyParents(true); setShowAddPlayersDialog(true); await fetchEnrolledPlayersForTournament(allCats, false);
  };
  const handleAddEnrolledPlayers = async () => {
    const hasCats = (tournament?.age_categories || []).length > 0;
    if (selectedEnrolledPlayerIds.length === 0 || (hasCats && !addPlayersCategory)) { toast.error(hasCats ? 'Please select players and category' : 'Please select players'); return; }
    setAddingPlayers(true);
    try {
      const catParam = addPlayersCategory ? `category=${addPlayersCategory}&` : '';
      const res = await api.post(`/tournaments/${id}/add-enrolled-players?${catParam}notify_parents=${notifyParents}`, selectedEnrolledPlayerIds);
      if (res.data.total_added > 0) toast.success(`${res.data.total_added} player(s) added${res.data.notifications_sent > 0 ? `, ${res.data.notifications_sent} notified` : ''}`);
      res.data.results.filter(r => r.status !== 'added').forEach(e => toast.warning(`${e.player_name || e.player_id}: ${e.message}`));
      setShowAddPlayersDialog(false); setSelectedEnrolledPlayerIds([]); fetchRegistrations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add players'); } finally { setAddingPlayers(false); }
  };

  // Manage Roster
  const openManagePlayersDialog = (team) => {
    setSelectedTeam(team);
    const currentRosterIds = team.player_ids || [];
    setSelectedPlayerIds([...currentRosterIds]);

    // Build the available list from THREE sources, deduped by player_id, so
    // the dialog always shows every current roster member (even if their
    // registration's age_category drifted, was unset, or the registration
    // is missing entirely):
    //   1. Registrations matching the team's age category — the eligible pool
    //      to add new players from.
    //   2. Registrations of players already on the roster (any age category).
    //   3. The team's own `players` array — covers cases where the player
    //      no longer has a registration row in this tournament.
    const teamAgeLc = (team.age_category || '').toLowerCase();
    // iter350 — a team with no age category (tournament created without
    // categories) can draw from ALL registered players; otherwise restrict
    // to the team's own category.
    const ageMatch = teamAgeLc
      ? (registrations || []).filter((r) => (r.age_category || '').toLowerCase() === teamAgeLc)
      : (registrations || []);
    const rosterFromRegistrations = (registrations || []).filter(
      (r) => currentRosterIds.includes(r.player_id)
    );
    const rosterFromTeamPlayers = (team.players || []).map((p) => ({
      player_id: p.player_id || p.id,
      player_name: p.player_name || p.full_name,
      parent_name: p.parent_name,
      parent_email: p.parent_email,
      payment_status: p.payment_status,
      age_category: team.age_category,
    }));

    const merged = new Map();
    [...ageMatch, ...rosterFromRegistrations, ...rosterFromTeamPlayers].forEach((p) => {
      const key = p.player_id || p.id;
      if (key && !merged.has(key)) merged.set(key, p);
    });
    setAvailablePlayers(Array.from(merged.values()));
    setShowManagePlayersDialog(true);
  };
  const handleTogglePlayer = (playerId) => {
    if (!selectedPlayerIds.includes(playerId)) {
      const otherTeams = teams.filter(t => t.id !== selectedTeam?.id && t.age_category === selectedTeam?.age_category && t.player_ids?.includes(playerId));
      if (otherTeams.length > 0) { toast.error(`Player already on "${otherTeams[0].name}" in ${selectedTeam?.age_category?.toUpperCase()}`); return; }
    }
    setSelectedPlayerIds(prev => prev.includes(playerId) ? prev.filter(i => i !== playerId) : [...prev, playerId]);
  };
  const handleSaveTeamPlayers = async () => {
    try {
      const current = selectedTeam?.player_ids || [];
      const toAdd = selectedPlayerIds.filter(i => !current.includes(i));
      const toRemove = current.filter(i => !selectedPlayerIds.includes(i));
      if (toAdd.length > 0) await api.post(`/tournaments/${id}/teams/${selectedTeam.id}/players`, { player_ids: toAdd });
      if (toRemove.length > 0) await api.delete(`/tournaments/${id}/teams/${selectedTeam.id}/players`, { data: { player_ids: toRemove } });
      toast.success('Team roster updated'); setShowManagePlayersDialog(false); fetchTeams();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update roster'); }
  };

  // Export
  const [reportLoading, setReportLoading] = useState(false);
  const handleExportGamesReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get(`/tournaments/${id}/export/games-report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(tournament?.name || 'tournament').replace(/[^a-z0-9]+/gi, '_')}_games_report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Games & cost report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportTeam = async () => {
    if (!exportTeam) return;
    const selectedFields = Object.entries(exportFields).filter(([, v]) => v).map(([k]) => k);
    if (selectedFields.length === 0) { toast.error('Select at least one field'); return; }
    setExportLoading(true);
    try {
      const res = await api.post(`/tournaments/${id}/teams/${exportTeam.id}/export`, { fields: selectedFields });
      const { headers, rows, team_name, tournament_name } = res.data;
      let csv = headers.join(',') + '\n';
      rows.forEach(row => { csv += row.map(c => { const v = String(c ?? ''); return (v.includes(',') || v.includes('"') || v.includes('\n')) ? `"${v.replace(/"/g, '""')}"` : v; }).join(',') + '\n'; });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${tournament_name}_${team_name}_roster.csv`; document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success(`Exported ${rows.length} players`); setShowExportDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to export'); } finally { setExportLoading(false); }
  };

  const handleRemovePlayerFromTeam = async (team, player) => {
    if (team.is_locked) {
      toast.error('Team is locked. Unlock the team first to edit its roster.');
      return;
    }
    const playerName = player.player_name || player.full_name || 'this player';
    const confirmed = window.confirm(
      `Remove ${playerName} from "${team.name}"?\n\nThey will stay registered for the tournament and can be assigned to another team.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/tournaments/${id}/teams/${team.id}/players`, {
        data: { player_ids: [player.player_id || player.id] },
      });
      toast.success(`${playerName} removed from ${team.name}. Tournament registration kept.`);
      fetchTeams();
      fetchRegistrations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove player from team');
    }
  };

  const handleToggleTeamLock = async (team) => {
    const action = team.is_locked ? 'unlock' : 'lock';
    try { await api.post(`/tournaments/${id}/teams/${team.id}/${action}`); toast.success(team.is_locked ? `RSVPs opened for '${team.name}'` : `RSVPs closed for '${team.name}'`); fetchTournament(); } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action} RSVPs`); }
  };

  // ==================== Render ====================
  if (loading || !tournament) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* iter271: extracted to <TournamentDetailHeader /> — keeps the
            page focused on data + tab orchestration. */}
        <TournamentDetailHeader
          tournament={tournament}
          tournamentsListPath={tournamentsListPath}
          isStaffOrAdmin={isStaffOrAdmin}
          unpaidCount={unpaidCount}
          onEditTournament={() => tournamentEdit.openEditDialog(tournament)}
          onAddPlayers={openAddPlayersDialog}
          onRegistrationsRefresh={fetchRegistrations}
          onTeamsRefresh={fetchTeams}
        />

        {/* iter271: extracted to <TournamentStats /> — stats grid + age
            categories strip. */}
        <TournamentStats
          tournament={tournament}
          totalRegistered={totalRegistered}
          paidCount={paidCount}
          unpaidCount={unpaidCount}
          totalCollected={totalCollected}
          teamsCount={teams.length}
        />

        {/* Main Tabs */}
        <Tabs
          defaultValue="registrations"
          className="space-y-4"
          onValueChange={(v) => {
            if ((v === 'player-summary' || v === 'cost-allocation') && !reportData) {
              fetchReportData();
            }
          }}
        >
          <TabsList className="flex w-full overflow-x-auto">
            <TabsTrigger value="registrations" data-testid="registrations-tab" className="text-xs sm:text-sm whitespace-nowrap"><Users className="h-4 w-4 mr-1 shrink-0" />Registrations ({registrations.length})</TabsTrigger>
            <TabsTrigger value="teams" data-testid="teams-tab" className="text-xs sm:text-sm whitespace-nowrap"><Shield className="h-4 w-4 mr-1 shrink-0" />Teams ({teams.length})</TabsTrigger>
            <TabsTrigger value="games" data-testid="games-tab" className="text-xs sm:text-sm whitespace-nowrap"><Clock className="h-4 w-4 mr-1 shrink-0" />Games</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="expenses-tab" className="text-xs sm:text-sm whitespace-nowrap"><Receipt className="h-4 w-4 mr-1 shrink-0" />Expenses ({expenses.length})</TabsTrigger>
            <TabsTrigger value="player-summary" data-testid="player-summary-tab" className="text-xs sm:text-sm whitespace-nowrap"><BarChart3 className="h-4 w-4 mr-1 shrink-0" />Player Summary</TabsTrigger>
            <TabsTrigger value="cost-allocation" data-testid="cost-allocation-tab" className="text-xs sm:text-sm whitespace-nowrap"><Coins className="h-4 w-4 mr-1 shrink-0" />Cost Allocation</TabsTrigger>
            <TabsTrigger value="summary" data-testid="summary-tab" className="text-xs sm:text-sm whitespace-nowrap"><DollarSign className="h-4 w-4 mr-1 shrink-0" />Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="registrations">
            <RegistrationsTab tournament={tournament} registrations={registrations} filteredRegistrations={filteredRegistrations} teams={teams}
              selectedRegistrations={selectedRegistrations} setSelectedRegistrations={setSelectedRegistrations} isAdmin={isAdmin} isStaff={isStaff} isCoach={isCoach} tournamentId={id}
              ageFilter={registrationsAgeFilter} setAgeFilter={setRegistrationsAgeFilter} paymentFilter={registrationsPaymentFilter} setPaymentFilter={setRegistrationsPaymentFilter}
              teamFilter={registrationsTeamFilter} setTeamFilter={setRegistrationsTeamFilter} sortField={registrationsSortField} setSortField={setRegistrationsSortField}
              sortOrder={registrationsSortOrder} setSortOrder={setRegistrationsSortOrder}
              onOpenPaymentDialog={openPaymentDialog} onOpenAddToTeamDialog={openAddToTeamDialog} onOpenDeleteRegistrationDialog={openDeleteRegistrationDialog}
              onBulkPaymentReminder={handleBulkPaymentReminder} onOpenBulkAddToTeam={() => setBulkAddToTeamDialog(true)} onOpenBulkMarkPaid={() => setBulkMarkPaidDialog(true)}
              onOpenRefundDialog={(reg) => { setRefundRegistration(reg); setRefundOpen(true); }}
              showArchived={showArchived} setShowArchived={setShowArchived}
              fetchRegistrations={fetchRegistrations} />
          </TabsContent>

          <TabsContent value="teams">
            <TeamsTab tournament={tournament} teams={teams} filteredTeams={filteredTeams} expandedTeamId={expandedTeamId} setExpandedTeamId={setExpandedTeamId}
              teamsAgeFilter={teamsAgeFilter} setTeamsAgeFilter={setTeamsAgeFilter} teamRosterSort={teamRosterSort} toggleRosterSort={toggleRosterSort} sortTeamRoster={sortTeamRoster}
              onCreateTeam={openCreateTeamDialog} onEditTeam={openEditTeamDialog} onDeleteTeam={openDeleteTeamConfirmation}
              onManagePlayers={openManagePlayersDialog} onExportTeam={(team) => { setExportTeam(team); setShowExportDialog(true); }}
              onToggleTeamLock={handleToggleTeamLock} onOpenChat={(teamId) => setTeamChatOpen(teamId)}
              onRemovePlayerFromTeam={handleRemovePlayerFromTeam} />
          </TabsContent>

          <TabsContent value="games">
            <div className="flex justify-end mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportGamesReport}
                disabled={reportLoading}
                data-testid="export-games-report-btn"
                title="Download an Excel report: games played, RSVPs, and expense split per player"
              >
                <Download className="h-4 w-4 mr-2" />
                {reportLoading ? 'Preparing…' : 'Export Games & Cost Report'}
              </Button>
            </div>
            <TournamentGames tournament={tournament} />
          </TabsContent>

          <TabsContent value="expenses">
            <ExpensesTab tournament={tournament} teams={teams} expenses={expenses}
              expenseCategories={expenseCategories}
              onAddExpense={openAddExpenseDialog} onEditExpense={openEditExpenseDialog} onDeleteExpense={handleDeleteExpense}
              onManageCategories={() => setShowManageCategoriesDialog(true)} isAdmin={isAdmin} />
          </TabsContent>

          <TabsContent value="player-summary">
            <PlayerSummaryTab
              tournament={tournament}
              rows={reportData?.player_summary || []}
              teams={teams}
              loading={reportDataLoading}
              onRefresh={fetchReportData}
            />
          </TabsContent>

          <TabsContent value="cost-allocation">
            <CostAllocationTab
              tournament={tournament}
              rows={reportData?.cost_allocation || []}
              totals={reportData?.totals}
              loading={reportDataLoading}
              onRefresh={fetchReportData}
            />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab tournament={tournament} registrations={registrations} totalCollected={totalCollected} expenseSummary={expenseSummary} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <PaymentDialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog} selectedRegistration={selectedRegistration} paymentForm={paymentForm} setPaymentForm={setPaymentForm} onConfirm={handleMarkPaid} />
      <ExpenseDialog open={showExpenseDialog} onOpenChange={(open) => { setShowExpenseDialog(open); if (!open) setEditingExpense(null); }} editingExpense={editingExpense} expenseForm={expenseForm} setExpenseForm={setExpenseForm} tournament={tournament} teams={teams} expenseCategories={expenseCategories} onSave={handleAddExpense} />
      <TeamDialog open={showTeamDialog} onOpenChange={setShowTeamDialog} selectedTeam={selectedTeam} teamForm={teamForm} setTeamForm={setTeamForm} tournament={tournament} coaches={coaches} onSave={selectedTeam ? handleUpdateTeam : handleCreateTeam} />
      <AddPlayersDialog open={showAddPlayersDialog} onOpenChange={setShowAddPlayersDialog} tournament={tournament} enrolledPlayers={enrolledPlayers} selectedPlayerIds={selectedEnrolledPlayerIds}
        loadingPlayers={loadingEnrolledPlayers} addingPlayers={addingPlayers} categories={addPlayersCategories} setCategories={setAddPlayersCategories}
        activeCategory={addPlayersCategory} setActiveCategory={setAddPlayersCategory} searchTerm={playerSearchTerm} setSearchTerm={setPlayerSearchTerm}
        notifyParents={notifyParents} setNotifyParents={setNotifyParents} sortField={playerSortField} setSortField={setPlayerSortField}
        sortOrder={playerSortOrder} setSortOrder={setPlayerSortOrder}
        onTogglePlayer={(pid) => setSelectedEnrolledPlayerIds(p => p.includes(pid) ? p.filter(i => i !== pid) : [...p, pid])}
        onSelectAll={() => { if (selectedEnrolledPlayerIds.length === enrolledPlayers.length) setSelectedEnrolledPlayerIds([]); else setSelectedEnrolledPlayerIds(enrolledPlayers.map(p => p.id)); }}
        onFetchPlayers={fetchEnrolledPlayersForTournament}
        includeUnenrolled={includeUnenrolledPlayers}
        setIncludeUnenrolled={(val) => {
          setIncludeUnenrolledPlayers(val);
          fetchEnrolledPlayersForTournament(addPlayersCategories, val);
        }}
        onSubmit={handleAddEnrolledPlayers} />
      <ManageRosterDialog open={showManagePlayersDialog} onOpenChange={setShowManagePlayersDialog} selectedTeam={selectedTeam}
        availablePlayers={availablePlayers} selectedPlayerIds={selectedPlayerIds} onTogglePlayer={handleTogglePlayer} onSave={handleSaveTeamPlayers}
        sortState={managePlayersSort} onToggleSort={toggleManagePlayersSort} sortPlayers={sortAvailablePlayers} />
      <DeleteTeamDialog open={deleteTeamConfirmOpen} onOpenChange={setDeleteTeamConfirmOpen} teamToDelete={teamToDelete} teamHasPlayers={teamHasPlayers} teamPlayerCount={teamPlayerCount} deletingTeam={deletingTeam} onConfirm={handleConfirmDeleteTeam} />
      <DeleteRegistrationDialog open={deleteRegistrationDialog} onOpenChange={(open) => { if (!open) { setDeleteRegistrationDialog(false); setRegistrationToDelete(null); setRegistrationDetails(null); setRefundCheckbox(false); setDeleteNotes(''); } }}
        registrationToDelete={registrationToDelete} registrationDetails={registrationDetails} loadingRegistrationDetails={loadingRegistrationDetails}
        deletingRegistration={deletingRegistration} refundCheckbox={refundCheckbox} setRefundCheckbox={setRefundCheckbox}
        deleteNotes={deleteNotes} setDeleteNotes={setDeleteNotes} onConfirm={handleDeleteRegistration} />
      <AddToTeamDialog open={addToTeamDialog} onOpenChange={(open) => { if (!open) { setAddToTeamDialog(false); setAddToTeamRegistration(null); setAddToTeamWarnings([]); setAddToTeamRequiresOverride(false); } }}
        registration={addToTeamRegistration} teams={teams} selectedTeam={addToTeamSelectedTeam}
        setSelectedTeam={(v) => { setAddToTeamSelectedTeam(v); setAddToTeamWarnings([]); setAddToTeamRequiresOverride(false); }}
        warnings={addToTeamWarnings} requiresOverride={addToTeamRequiresOverride} loading={addToTeamLoading} onAdd={handleAddToTeam} getTeamsForRegistration={getTeamsForRegistration} />
      <BulkAddToTeamDialog open={bulkAddToTeamDialog} onOpenChange={(open) => { if (!open) { setBulkAddToTeamDialog(false); setBulkAddToTeamSelectedTeam(''); setBulkAddToTeamOverride(false); } }}
        selectedCount={selectedRegistrations.length} teams={teams} selectedTeam={bulkAddToTeamSelectedTeam} setSelectedTeam={setBulkAddToTeamSelectedTeam}
        overrideWarnings={bulkAddToTeamOverride} setOverrideWarnings={setBulkAddToTeamOverride} loading={bulkAddToTeamLoading} onSubmit={handleBulkAddToTeam} />
      <BulkMarkPaidDialog open={bulkMarkPaidDialog} onOpenChange={(open) => { if (!open) { setBulkMarkPaidDialog(false); setBulkMarkPaidMethod(''); setBulkMarkPaidNotes(''); } }}
        selectedCount={selectedRegistrations.length} tournament={tournament} method={bulkMarkPaidMethod} setMethod={setBulkMarkPaidMethod}
        notes={bulkMarkPaidNotes} setNotes={setBulkMarkPaidNotes} loading={bulkMarkPaidLoading} onSubmit={handleBulkMarkPaid} />
      <ExportRosterDialog open={showExportDialog} onOpenChange={(open) => { if (!open) { setShowExportDialog(false); setExportTeam(null); } }}
        exportTeam={exportTeam} exportFields={exportFields} setExportFields={setExportFields} loading={exportLoading} onExport={handleExportTeam} />
      <Dialog open={!!teamChatOpen} onOpenChange={(open) => !open && setTeamChatOpen(null)}>
        <DialogContent className="max-w-4xl h-[85vh] p-0">
          {teamChatOpen && <TeamChat teamId={teamChatOpen} teamName={teams.find(t => t.id === teamChatOpen)?.name || 'Team Chat'} onBack={() => setTeamChatOpen(null)} />}
        </DialogContent>
      </Dialog>
      <ManageExpenseCategoriesDialog open={showManageCategoriesDialog} onOpenChange={setShowManageCategoriesDialog} categories={expenseCategories} onCategoriesChanged={fetchExpenseCategories} />
      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        entityType="tournament_registration"
        entity={refundRegistration}
        priceField="payment_amount"
        summaryLines={
          refundRegistration
            ? [
                { label: 'Player', value: refundRegistration.player_name || '—' },
                { label: 'Tournament', value: tournament?.name || tournament?.title || '—' },
              ]
            : []
        }
        allowCancelCheckbox={false}
        onRefunded={() => { setRefundOpen(false); setRefundRegistration(null); fetchRegistrations(); }}
      />

      {/* iter270: Edit Tournament dialog (with iter240 amount-change cascade) */}
      <AdminTournamentFormDialog {...tournamentEdit.dialogProps} />
      <TournamentAmountChangeConfirmDialog
        open={tournamentEdit.amountChange.open}
        onOpenChange={tournamentEdit.amountChange.setOpen}
        preview={tournamentEdit.amountChange.preview}
        onConfirm={tournamentEdit.amountChange.onConfirm}
        submitting={tournamentEdit.amountChange.submitting}
      />
    </DashboardLayout>
  );
};

export default TournamentDetail;
